import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Container, Divider, Grid2, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    keyTransferPageInitialLoadAsync,
    loadTransferHistoryAsync,
    selectState,
    setDestinationCompanyAsync,
    setDestinationProductAsync,
    setRequestedNumberOfKeys,
    setSourceCompanyAsync,
    setSourceProductAsync,
    transferKeysAsync,
} from "./KeyTransferPageSlice";
import { DropDownSelect } from "../../components/drop_down/DropDown";
import { CssTextField } from "../../components/custom_input/CustomInput";
import { ExpandMoreOutlined, RefreshOutlined, SwapHorizOutlined } from "@mui/icons-material";

/* Dates come back from Firestore as Timestamps even though the models type them
   as Date, so handle both. */
function formatDate(value: any): string {
    if (value == null) return "Unknown date";
    if (typeof value.toDate === "function") return value.toDate().toLocaleString();
    if (value instanceof Date) return value.toLocaleString();
    return `${value}`;
}

export function KeyTransferPage() {

    const theme = useTheme();
    const lessThanSm = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectState);

    useEffect(() => {
        dispatch(keyTransferPageInitialLoadAsync());
    }, []);

    const sameProductChosen = state.sourceProduct != null
        && state.destinationProduct != null
        && state.sourceProduct.guid === state.destinationProduct.guid;

    const readyToTransfer = state.sourceProduct != null
        && state.destinationProduct != null
        && !sameProductChosen
        && state.requestedNumberOfKeys > 0
        && state.requestedNumberOfKeys <= state.sourceAvailableKeyCount;

    return (
        <Container maxWidth="sm" disableGutters={lessThanSm}>

            <Box
                sx={{
                    height: "100vh",
                    display: state.isLoading ? "flex" : "none",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center",
                }}
            >
                <Grid2
                    container
                    direction="column"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid2>
                        <CircularProgress size={60} />
                    </Grid2>
                    <Grid2>
                        <Typography>Processing</Typography>
                    </Grid2>
                </Grid2>
            </Box>

            <Box
                sx={{
                    minHeight: "100vh",
                    display: state.isLoading ? "none" : "flex",
                    alignItems: "start",
                }}
            >
                <Grid2
                    container
                    spacing={2}
                    direction="column"
                    sx={{ mt: 2 }}
                    columns={{ xs: 6, sm: 6, md: 12 }}
                >
                    <Typography variant="h2">Transfer product keys</Typography>

                    {state.loadError != null &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            {state.loadError}
                        </Typography>
                    }
                    <Typography margin={1}>
                        Moves unassigned product keys from one product to another so a product
                        that has run out can be served from another product's spare stock.
                        Keys that have already been issued to a customer are never moved.
                    </Typography>

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    <Typography variant="h3">Take keys from</Typography>
                    <Grid2>
                        <DropDownSelect
                            label="Company"
                            value={state.sourceCompany}
                            values={state.availableCompanies}
                            displayMember="name"
                            onChange={(x) => {
                                dispatch(setSourceCompanyAsync(x.target.value));
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <DropDownSelect
                            label="Product"
                            value={state.sourceProduct}
                            values={state.sourceProducts}
                            displayMember="name"
                            onChange={(x) => {
                                dispatch(setSourceProductAsync(x.target.value));
                            }}
                        />
                    </Grid2>
                    {state.sourceProduct != null &&
                        <Grid2>
                            <Typography margin={1}>Available keys: {state.sourceAvailableKeyCount}</Typography>
                            <Typography margin={1}>Outstanding vouchers: {state.sourceOutstandingVoucherCount}</Typography>
                        </Grid2>
                    }

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    <Typography variant="h3">Give keys to</Typography>
                    <Grid2>
                        <DropDownSelect
                            label="Company"
                            value={state.destinationCompany}
                            values={state.availableCompanies}
                            displayMember="name"
                            onChange={(x) => {
                                dispatch(setDestinationCompanyAsync(x.target.value));
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <DropDownSelect
                            label="Product"
                            value={state.destinationProduct}
                            values={state.destinationProducts}
                            displayMember="name"
                            onChange={(x) => {
                                dispatch(setDestinationProductAsync(x.target.value));
                            }}
                        />
                    </Grid2>
                    {state.destinationProduct != null &&
                        <Grid2>
                            <Typography margin={1}>Available keys: {state.destinationAvailableKeyCount}</Typography>
                            <Typography margin={1}>Outstanding vouchers: {state.destinationOutstandingVoucherCount}</Typography>
                        </Grid2>
                    }

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    <Grid2>
                        <CssTextField
                            label="Number of keys"
                            type="number"
                            value={state.requestedNumberOfKeys === 0 ? "" : state.requestedNumberOfKeys}
                            onChange={(e) => {
                                var parsed = parseInt(e.target.value, 10);
                                dispatch(setRequestedNumberOfKeys(isNaN(parsed) || parsed < 0 ? 0 : parsed));
                            }}
                        />
                    </Grid2>

                    {state.destinationProduct != null && state.destinationOutstandingVoucherCount > state.destinationAvailableKeyCount &&
                        <Typography margin={1}>
                            {state.destinationCompany?.name} needs {state.destinationOutstandingVoucherCount - state.destinationAvailableKeyCount} more
                            key(s) to cover every outstanding voucher.
                        </Typography>
                    }

                    {sameProductChosen &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            The source and destination product are the same.
                        </Typography>
                    }

                    {state.requestedNumberOfKeys > state.sourceAvailableKeyCount &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            Only {state.sourceAvailableKeyCount} key(s) are available to transfer.
                        </Typography>
                    }

                    {readyToTransfer && state.requestedNumberOfKeys > (state.sourceAvailableKeyCount - state.sourceOutstandingVoucherCount) &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            This leaves {state.sourceCompany?.name} short: {state.sourceAvailableKeyCount - state.requestedNumberOfKeys} key(s)
                            remaining against {state.sourceOutstandingVoucherCount} outstanding voucher(s).
                        </Typography>
                    }

                    <Grid2>
                        <Button
                            component="label"
                            variant="contained"
                            disabled={!readyToTransfer}
                            startIcon={<SwapHorizOutlined sx={{ color: "#FFFFFF" }} />}
                            onClick={(e: any) => {
                                dispatch(transferKeysAsync());
                            }}
                        >
                            <Typography variant="h5">Transfer</Typography>
                        </Button>
                    </Grid2>

                    {state.transferError != null &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            {state.transferError}
                        </Typography>
                    }

                    {state.transferResultMessage != null &&
                        <Typography margin={1} sx={{ fontWeight: "bold" }}>
                            {state.transferResultMessage}
                        </Typography>
                    }

                    <Divider sx={{ mt: 4, mb: 2 }} />

                    <Typography variant="h2">Transfer history</Typography>

                    <Grid2>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<RefreshOutlined sx={{ color: "#FFFFFF" }} />}
                            onClick={(e: any) => {
                                dispatch(loadTransferHistoryAsync());
                            }}
                        >
                            <Typography variant="h5">Refresh</Typography>
                        </Button>
                    </Grid2>

                    {state.transferHistoryError != null &&
                        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
                            {state.transferHistoryError}
                        </Typography>
                    }

                    {state.transferHistory.length === 0 && state.transferHistoryError == null && state.loadError == null &&
                        <Typography margin={1}>No keys have been transferred yet.</Typography>
                    }

                    <Grid2 sx={{ mb: 4 }}>
                        {state.transferHistory.map((transfer, index) => (
                            <Accordion key={transfer.guid ?? index}>
                                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                                    <Grid2>
                                        <Typography sx={{ fontWeight: "bold" }}>
                                            {transfer.transferredKeyCount} key(s): {transfer.sourceCompanyName} - {transfer.sourceProductName}
                                            {" → "}
                                            {transfer.destinationCompanyName} - {transfer.destinationProductName}
                                        </Typography>
                                        <Typography>{formatDate(transfer.transferredDate)}</Typography>
                                    </Grid2>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography margin={1}>Performed by: {transfer.performedByEmail ?? "Unknown"}</Typography>
                                    {transfer.transferredKeyCount !== transfer.requestedKeyCount &&
                                        <Typography margin={1}>
                                            Requested {transfer.requestedKeyCount}, transferred {transfer.transferredKeyCount}.
                                        </Typography>
                                    }
                                    <Typography margin={1} sx={{ fontWeight: "bold" }}>Keys moved</Typography>
                                    {transfer.transferredKeysTruncated &&
                                        <Typography margin={1}>
                                            Showing the first {transfer.transferredKeys.length} of {transfer.transferredKeyCount} keys.
                                        </Typography>
                                    }
                                    <Typography margin={1} sx={{ wordBreak: "break-all" }}>
                                        {transfer.transferredKeys.join(", ")}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid2>
                </Grid2>
            </Box>
        </Container>
    );
}
